import { EonUiIcon } from "@eon-ui/eon-ui-components-react";
import { useTranslation } from "@me8eon/translation";
import { useJwtToken } from "@me8eon/react_authentication";
import useOnboardingPortalStore from "../OnboardingPortal.Store";
import { useEffect, useMemo } from "react";
import OnboardingPortalLinkArrow from "../components/OnboardingPortalLinkArrow";
import OnboardingPortalQuickActions from "../components/OnboardingPortalQuickActions";
import OnboardingPortalAppIcon from '../components/OnboardingPortalAppIcon';
import OnboardingPortalTableHeaders from '../components/OnboardingPortalTableHeaders';
import { useLanguage } from "@me8eon/language";
import { Status } from "../OnboardingPortal.types";

const REVIEW_STATUS_LIST: Status[] = [
    "pending",
    "approved",
    "inQA",
    "inProd",
    "rejected",
];

const ReviewerDashboardStatsWrapper = ({
    t,
    items,
}: {
    t: (key: string) => string;
    items: { status: Status }[];
}) => (
    <div className="onboarding-portal--home-screen--content--stats--card--stats-wrapper">
        {REVIEW_STATUS_LIST.map((status) => (
            <div
                key={status}
                className="onboarding-portal--home-screen--content--stats--card--stats-wrapper--item"
            >
                <div className="onboarding-portal--home-screen--content--stats--card--stats-wrapper--item--value">
                    {
                        items.filter((item) => item.status === status)
                            .length
                    }
                </div>
                <span
                    className={`onboarding-portal--home-screen--content--stats--card--stats-wrapper--item--label status--${status}`}
                >
                    {t(`onboardingPortal.statusLabel.${status}`)}
                </span>
            </div>
        ))}
    </div>
);

const HomeScreenEmptyStateScreen = ({
    isAppsEmpty,
    isQnAEmpty,
}: {
    isAppsEmpty: boolean;
    isQnAEmpty: boolean;
}) => {
    const [language] = useLanguage()
    const t = useTranslation();
    const title = useMemo(() => {
        if (isAppsEmpty && isQnAEmpty)
            return t("onboardingPortal.noData.reviewDashboardEmpty");
        if (isAppsEmpty) return t("onboardingPortal.noData.reviewAppsEmpty");
        if (isQnAEmpty) return t("onboardingPortal.noData.reviewQnAEmpty");
        return "";
    }, [isAppsEmpty, isQnAEmpty, language]);
    return (
        <>
            <div
                className={
                    isAppsEmpty && isQnAEmpty
                        ? "onboarding-portal--home-screen--empty-state"
                        : "onboarding-portal--home-screen--empty-state--card"
                }
            >
                <div className="onboarding-portal--home-screen--empty-state--text">
                    <EonUiIcon name="info" scheme="red500" />
                    <span>{title}</span>
                </div>
            </div>
        </>
    );
};

const ReviewerDashboard = () => {
    const t = useTranslation();
    const {
        setShowModule,
        fetchReviewApps,
        fetchReviewQnA,
        reviewApps,
        reviewQnA,
        reviewAllApps,
        reviewAllQnA,
        onReviewApp,
        onReviewQnA,
        handleReviewApp,
        handleReviewQnA,
    } = useOnboardingPortalStore();
    const jwt = useJwtToken();

    useEffect(() => {
        if (fetchReviewApps) fetchReviewApps(jwt);
        if (fetchReviewQnA) fetchReviewQnA(jwt);
    }, [fetchReviewApps, fetchReviewQnA, jwt]);
    return (
        <>
            {reviewAllApps.length === 0 && reviewAllQnA.length === 0 ? (
                <HomeScreenEmptyStateScreen isAppsEmpty isQnAEmpty />
            ) : (
                <div className="onboarding-portal--home-screen">
                    <div className="onboarding-portal--home-screen--content">
                        <div className="onboarding-portal--home-screen--content--stats">
                            <div className="onboarding-portal--home-screen--content--stats--card">
                                <div className="onboarding-portal--home-screen--content--stats--card--header">
                                    <div className="onboarding-portal--home-screen--content--stats--card--header--text">
                                        {t(
                                            "onboardingPortal.tableHeaders.apps"
                                        )}
                                    </div>
                                    <OnboardingPortalLinkArrow
                                        onClick={() =>
                                            setShowModule("review-apps")
                                        }
                                    />
                                </div>
                                <div className="onboarding-portal--home-screen--content--stats--card--title-wrapper">
                                    <div className="onboarding-portal--home-screen--content--stats--card--title">
                                        {reviewAllApps.length}
                                    </div>
                                    <ReviewerDashboardStatsWrapper
                                        t={t}
                                        items={reviewAllApps}
                                    />
                                </div>
                            </div>
                            <div className="onboarding-portal--home-screen--content--stats--card">
                                <div className="onboarding-portal--home-screen--content--stats--card--header">
                                    <div className="onboarding-portal--home-screen--content--stats--card--header--text">
                                        {t("onboardingPortal.tableHeaders.qna")}
                                    </div>
                                    <OnboardingPortalLinkArrow
                                        onClick={() =>
                                            setShowModule("review-qna")
                                        }
                                    />
                                </div>
                                <div className="onboarding-portal--home-screen--content--stats--card--title-wrapper">
                                    <div className="onboarding-portal--home-screen--content--stats--card--title">
                                        {reviewAllQnA.length}
                                    </div>
                                    <ReviewerDashboardStatsWrapper
                                        t={t}
                                        items={reviewAllQnA}
                                    />
                                </div>
                            </div>
                        </div>
                        {reviewApps.length > 0 && (
                            <div className="onboarding-portal--content--card">
                                <div className="onboarding-portal--content--card--header">
                                    <div className="onboarding-portal--content--card--header--text">
                                        {t(
                                            "onboardingPortal.tableHeaders.apps"
                                        )}{" "}
                                        ({reviewApps.length})
                                    </div>
                                    <OnboardingPortalLinkArrow
                                        onClick={() =>
                                            setShowModule("review-apps")
                                        }
                                    />
                                </div>
                                <table className="onboarding-portal--table">
                                    <OnboardingPortalTableHeaders
                                        headers={[
                                            "onboardingPortal.tableHeaders.icon",
                                            "onboardingPortal.tableHeaders.title",
                                            "onboardingPortal.tableHeaders.subTitle",
                                            "onboardingPortal.tableHeaders.status",
                                            "onboardingPortal.tableHeaders.owner",
                                            "onboardingPortal.tableHeaders.action",
                                        ]}
                                        t={t}
                                    />
                                    <tbody>
                                        {reviewApps.slice(0, 3).map((app) => (
                                            <tr key={app.id}>
                                                <td>
                                                    <OnboardingPortalAppIcon
                                                        icon={app.icon}
                                                        iconUrl={
                                                            app.iconDataUrl
                                                        }
                                                    />
                                                </td>
                                                <td>{app.title}</td>
                                                <td>{app.subTitle}</td>
                                                <td>
                                                    <span
                                                        className={`status--${app.status}`}
                                                    >
                                                        {t(
                                                            `onboardingPortal.statusLabel.${app.status}`
                                                        )}
                                                    </span>
                                                </td>
                                                <td>{app.owner}</td>
                                                <td>
                                                    <div className="actions-row">
                                                        {app.status ===
                                                            "pending" && (
                                                            <>
                                                                <span
                                                                    className="action--approve"
                                                                    onClick={() =>
                                                                        onReviewApp(
                                                                            app
                                                                        )
                                                                    }
                                                                >
                                                                    {t(
                                                                        "onboardingPortal.approveBtn"
                                                                    )}
                                                                </span>
                                                                <span
                                                                    className="action--reject"
                                                                    onClick={() =>
                                                                        onReviewApp(
                                                                            app
                                                                        )
                                                                    }
                                                                >
                                                                    {t(
                                                                        "onboardingPortal.rejectBtn"
                                                                    )}
                                                                </span>
                                                                <OnboardingPortalLinkArrow
                                                                    onClick={() => {
                                                                        onReviewApp(
                                                                            app
                                                                        );
                                                                    }}
                                                                    noSpace={
                                                                        false
                                                                    }
                                                                    size="small"
                                                                />
                                                            </>
                                                        )}
                                                        {app.status ===
                                                            "approved" && (
                                                            <span
                                                                className="action--approve"
                                                                onClick={() => {
                                                                    handleReviewApp(
                                                                        jwt,
                                                                        app?.id ||
                                                                            "",
                                                                        "inQA",
                                                                        "Moved to QA"
                                                                    );
                                                                }}
                                                            >
                                                                {t(
                                                                    "onboardingPortal.toQABtn"
                                                                )}
                                                            </span>
                                                        )}
                                                        {app.status ===
                                                            "inQA" && (
                                                            <span
                                                                className="action--approve"
                                                                onClick={() => {
                                                                    handleReviewApp(
                                                                        jwt,
                                                                        app?.id ||
                                                                            "",
                                                                        "inProd",
                                                                        "Moved to Production"
                                                                    );
                                                                }}
                                                            >
                                                                {t(
                                                                    "onboardingPortal.toProdBtn"
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {reviewQnA.length > 0 && (
                            <div className="onboarding-portal--content--card">
                                <div className="onboarding-portal--content--card--header">
                                    <div className="onboarding-portal--content--card--header--text">
                                        {t("onboardingPortal.tableHeaders.qna")}{" "}
                                        ({reviewQnA.length})
                                    </div>
                                    <OnboardingPortalLinkArrow
                                        onClick={() =>
                                            setShowModule("review-qna")
                                        }
                                    />
                                </div>
                                <table className="onboarding-portal--table">
                                    <OnboardingPortalTableHeaders
                                        headers={[
                                            "onboardingPortal.tableHeaders.question",
                                            "onboardingPortal.tableHeaders.answer",
                                            "onboardingPortal.tableHeaders.category",
                                            "onboardingPortal.tableHeaders.status",
                                            "onboardingPortal.tableHeaders.owner",
                                            "onboardingPortal.tableHeaders.action",
                                        ]}
                                        t={t}
                                    />
                                    <tbody>
                                        {reviewQnA.slice(0, 3).map((qna) => (
                                            <tr key={qna.id}>
                                                <td>{qna.question}</td>
                                                <td>{qna.answer}</td>
                                                <td>{qna.category}</td>
                                                <td
                                                    className={`status--${qna.status}`}
                                                >
                                                    <span
                                                        className={`status--${qna.status}`}
                                                    >
                                                        {t(
                                                            `onboardingPortal.statusLabel.${qna.status}`
                                                        )}
                                                    </span>
                                                </td>
                                                <td>{qna.owner}</td>
                                                <td>
                                                    <div className="actions-row">
                                                        {qna.status ===
                                                            "pending" && (
                                                            <>
                                                                <span
                                                                    className="action--approve"
                                                                    onClick={() => {
                                                                        onReviewQnA(
                                                                            qna
                                                                        );
                                                                    }}
                                                                >
                                                                    {t(
                                                                        "onboardingPortal.approveBtn"
                                                                    )}
                                                                </span>
                                                                <span
                                                                    className="action--reject"
                                                                    onClick={() => {
                                                                        onReviewQnA(
                                                                            qna
                                                                        );
                                                                    }}
                                                                >
                                                                    {t(
                                                                        "onboardingPortal.rejectBtn"
                                                                    )}
                                                                </span>
                                                                <OnboardingPortalLinkArrow
                                                                    onClick={() => {
                                                                        onReviewQnA(
                                                                            qna
                                                                        );
                                                                    }}
                                                                    noSpace={
                                                                        false
                                                                    }
                                                                    size="small"
                                                                />
                                                            </>
                                                        )}
                                                        {qna.status ===
                                                            "approved" && (
                                                            <span
                                                                className="action--approve"
                                                                onClick={() => {
                                                                    handleReviewQnA(
                                                                        jwt,
                                                                        qna.id ||
                                                                            "",
                                                                        "inQA",
                                                                        "Moved to QA"
                                                                    );
                                                                }}
                                                            >
                                                                {t(
                                                                    "onboardingPortal.toQABtn"
                                                                )}
                                                            </span>
                                                        )}
                                                        {qna.status ===
                                                            "inQA" && (
                                                            <span
                                                                className="action--approve"
                                                                onClick={() => {
                                                                    handleReviewQnA(
                                                                        jwt,
                                                                        qna.id ||
                                                                            "",
                                                                        "inProd",
                                                                        "Moved to Production"
                                                                    );
                                                                }}
                                                            >
                                                                {t(
                                                                    "onboardingPortal.toProdBtn"
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {reviewApps.length === 0 && (
                            <HomeScreenEmptyStateScreen
                                isAppsEmpty
                                isQnAEmpty={false}
                            />
                        )}
                        {reviewQnA.length === 0 && (
                            <HomeScreenEmptyStateScreen
                                isAppsEmpty={false}
                                isQnAEmpty
                            />
                        )}
                    </div>
                    <div className="onboarding-portal--side-bar">
                        <OnboardingPortalQuickActions />
                    </div>
                </div>
            )}
        </>
    );
};

export default ReviewerDashboard;
